import type {
  Scenario,
  SimulationGraph,
  SimulationResultPayload,
  SimulationRunConfig,
  SummaryStatistics
} from '../../models/simulation';
import type { WorkspaceView } from '../../store/viewStore';
import { buildTornadoResult, findPrimaryOutputNode } from '../editor/utils/tornadoSensitivity';

interface BuildWorkspaceAssistantContextOptions {
  activeView: WorkspaceView;
  graph: SimulationGraph;
  runConfig: SimulationRunConfig;
  lastResult?: SimulationResultPayload;
  currentSimulationId?: string;
  currentSimulationName?: string;
  currentVersionId?: string;
  currentVersionName?: string;
  selectedNodeId?: string;
  scenarios: Scenario[];
  activeScenarioId: string | null;
  scenarioResults: Map<string, SimulationResultPayload>;
}

const pickStatistic = (
  summary: SummaryStatistics | undefined,
  statistic: string | undefined
): number | undefined => {
  if (!summary) {
    return undefined;
  }

  switch (statistic) {
    case 'median':
      return summary.median;
    case 'p10':
      return summary.p10;
    case 'p90':
      return summary.p90;
    case 'std':
      return summary.std;
    case 'value':
    case 'mean':
    default:
      return summary.mean;
  }
};

const summarizeResultOutputs = (
  graph: SimulationGraph,
  result?: SimulationResultPayload
) => {
  if (!result) {
    return {
      available: false,
      outputs: []
    };
  }

  const outputNodes = graph.nodes.filter((node) => node.data.isOutput === true);
  const nodes = outputNodes.length > 0
    ? outputNodes
    : graph.nodes.filter((node) => node.data.type === 'formula').slice(-5);

  return {
    available: true,
    graphId: result.graphId,
    durationMs: result.durationMs,
    runConfig: result.runConfig,
    outputs: nodes.map((node) => {
      const nodeResult = result.results[node.id];
      const statistic = node.data.statistic ?? 'mean';
      return {
        id: node.id,
        name: node.data.name,
        statistic,
        value: pickStatistic(nodeResult?.summary, statistic),
        unit: nodeResult?.unit,
        summary: nodeResult?.summary
      };
    })
  };
};

const summarizeScenarios = (
  graph: SimulationGraph,
  scenarios: Scenario[],
  activeScenarioId: string | null,
  scenarioResults: Map<string, SimulationResultPayload>
) => ({
  count: scenarios.length,
  activeScenarioId,
  items: scenarios.slice(0, 10).map((scenario) => {
    const result = scenarioResults.get(scenario.id);
    return {
      id: scenario.id,
      name: scenario.name,
      description: scenario.description,
      isBaseline: scenario.isBaseline,
      isActive: scenario.id === activeScenarioId,
      overrideCount: Object.keys(scenario.distributionOverrides ?? {}).length,
      selectedNodeIds: scenario.selectedNodeIds ?? [],
      graphDataUpdatedAt: scenario.graphDataUpdatedAt,
      results: summarizeResultOutputs(graph, result)
    };
  })
});

const summarizeTornado = (graph: SimulationGraph) => {
  const target = findPrimaryOutputNode(graph);
  const tornado = buildTornadoResult(graph, target?.id);

  return {
    target: target
      ? {
          id: target.id,
          name: target.data.name,
          isOutput: target.data.isOutput === true
        }
      : null,
    baselineValue: tornado.baselineValue,
    baselineUnit: tornado.baselineUnit,
    baselineError: tornado.baselineError,
    topDrivers: [...tornado.rows]
      .sort((a, b) => b.impactMagnitude - a.impactMagnitude)
      .slice(0, 8)
      .map((row) => ({
        id: row.id,
        name: row.name,
        tags: row.tags,
        unit: row.unit,
        p10: row.quantiles.p10,
        median: row.quantiles.median,
        p90: row.quantiles.p90,
        impactDelta: row.impactDelta,
        impactMagnitude: row.impactMagnitude,
        impactUnit: row.impactUnit,
        impactLowValue: row.impactLowValue,
        impactHighValue: row.impactHighValue,
        impactError: row.impactError
      }))
  };
};

export const buildWorkspaceAssistantContext = ({
  activeView,
  graph,
  runConfig,
  lastResult,
  currentSimulationId,
  currentSimulationName,
  currentVersionId,
  currentVersionName,
  selectedNodeId,
  scenarios,
  activeScenarioId,
  scenarioResults
}: BuildWorkspaceAssistantContextOptions) => ({
  activeView,
  simulation: {
    id: currentSimulationId,
    name: currentSimulationName ?? graph.name,
    versionId: currentVersionId,
    versionName: currentVersionName
  },
  graph: {
    id: graph.id,
    name: graph.name,
    nodeCount: graph.nodes.length,
    edgeCount: graph.edges.length,
    dataUpdatedAt: graph.dataUpdatedAt,
    customUnits: graph.customUnits ?? [],
    selectedNodeId,
    nodes: graph.nodes.map((node) => ({
      id: node.id,
      name: node.data.name,
      type: node.data.type,
      description: node.data.description,
      tags: node.data.tags ?? [],
      isOutput: node.data.isOutput === true,
      statistic: node.data.statistic,
      position: node.position,
      data: node.data
    })),
    edges: graph.edges
  },
  runConfig,
  results: summarizeResultOutputs(graph, lastResult),
  scenarios: summarizeScenarios(graph, scenarios, activeScenarioId, scenarioResults),
  tornado: summarizeTornado(graph)
});
